/* core-bundle.js — enforce the "core product" bundle invariant on the Shopify cart.
 *
 * Invariant: for every parent cart line carrying property `_has_core: <variant_id>`,
 * there must be at least `line.quantity` of a cart line whose variant_id is
 * `<variant_id>` AND whose property `_core_item == 'true'`.
 * Required totals aggregate across parent lines by core variant id.
 *
 * Entry hooks:
 *   - `cartcount:update` on window — emitted by Empire after every mutation.
 *   - `DOMContentLoaded` — one pass per page load.
 *   - A `fetch` wrapper — safety net for direct `/cart/*.js` calls.
 *   - The cart form's `submit` — reconcile before checkout, strip hostile
 *     `updates[]` entries targeting core lines.
 */
(function () {
  'use strict';
  if (window.__coreBundleInstalled) return;
  window.__coreBundleInstalled = true;

  const CART_URL = '/cart.js';
  const CART_CHANGE = '/cart/change.js';
  const CART_ADD = '/cart/add.js';
  const CART_PATHS = ['/cart/add.js', '/cart/change.js', '/cart/update.js', '/cart/clear.js'];

  // Prevent re-entrant reconcile loops when our own fetches fire the shim.
  let reconciling = false;
  let pendingPass = false;

  function debug() {
    if (!window.__coreBundleDebug) return;
    console.log('[core-bundle]', ...arguments);
  }

  async function getCart() {
    const resp = await fetch(CART_URL, { method: 'GET', credentials: 'same-origin' });
    if (!resp.ok) throw new Error('cart fetch failed: ' + resp.status);
    return resp.json();
  }

  /** Return a Map<coreVariantId, totalRequiredQty>. */
  function computeRequired(cart) {
    const required = new Map();
    for (const item of cart.items) {
      const has = item.properties && item.properties._has_core;
      if (!has) continue;
      const coreId = parseInt(has, 10);
      if (!coreId) continue;
      required.set(coreId, (required.get(coreId) || 0) + item.quantity);
    }
    return required;
  }

  /** Return Map<coreVariantId, { total, lines: [{key, quantity}] }>. */
  function computeActual(cart) {
    const actual = new Map();
    for (const item of cart.items) {
      const isCore = item.properties && item.properties._core_item === 'true';
      if (!isCore) continue;
      const bucket = actual.get(item.variant_id) || { total: 0, lines: [] };
      bucket.total += item.quantity;
      bucket.lines.push({ key: item.key, quantity: item.quantity });
      actual.set(item.variant_id, bucket);
    }
    return actual;
  }

  async function changeLine(lineKey, quantity) {
    const resp = await fetch(CART_CHANGE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!resp.ok) throw new Error('cart change failed: ' + resp.status);
    return resp.json();
  }

  async function addCore(coreVariantId, quantity, parentHandle, parentTitle) {
    const resp = await fetch(CART_ADD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: coreVariantId,
          quantity,
          properties: {
            _core_item: 'true',
            _core_for_handle: parentHandle || '',
            _core_for_title: parentTitle || ''
          }
        }]
      })
    });
    if (!resp.ok) throw new Error('cart add (core) failed: ' + resp.status);
    return resp.json();
  }

  /** Pick a representative parent for context when creating a missing core line. */
  function findParentFor(cart, coreVariantId) {
    for (const item of cart.items) {
      const has = item.properties && item.properties._has_core;
      if (has && parseInt(has, 10) === coreVariantId) {
        return { handle: item.handle, title: item.product_title || item.title };
      }
    }
    return { handle: '', title: '' };
  }

  /**
   * One reconciliation pass. Returns { changed, cart } where `cart` is the
   * final cart JSON and `changed` is true if we actually mutated anything.
   */
  async function reconcileOnce() {
    const cart = await getCart();
    const required = computeRequired(cart);
    const actual = computeActual(cart);

    let changed = false;

    // 1. Required cores missing or short.
    for (const [variantId, needed] of required.entries()) {
      const have = actual.get(variantId) || { total: 0, lines: [] };
      if (have.total === needed) continue;

      if (have.total === 0) {
        const parent = findParentFor(cart, variantId);
        debug('adding missing core', variantId, 'x', needed);
        await addCore(variantId, needed, parent.handle, parent.title);
        changed = true;
      } else if (have.total < needed) {
        const target = have.lines[0];
        const delta = needed - have.total;
        debug('bumping core', variantId, 'to', target.quantity + delta);
        await changeLine(target.key, target.quantity + delta);
        changed = true;
      } else {
        let surplus = have.total - needed;
        for (const line of have.lines) {
          if (surplus <= 0) break;
          const drop = Math.min(surplus, line.quantity);
          const nextQty = line.quantity - drop;
          debug('trimming core', variantId, 'line', line.key, '->', nextQty);
          await changeLine(line.key, nextQty);
          surplus -= drop;
          changed = true;
        }
      }
    }

    // 2. Orphan core lines (no required parent).
    for (const [variantId, bucket] of actual.entries()) {
      if (required.has(variantId)) continue;
      for (const line of bucket.lines) {
        debug('removing orphan core line', line.key);
        await changeLine(line.key, 0);
        changed = true;
      }
    }

    const finalCart = changed ? await getCart() : cart;
    return { changed, cart: finalCart };
  }

  async function reconcile() {
    if (reconciling) { pendingPass = true; return null; }
    reconciling = true;
    try {
      const result = await reconcileOnce();
      if (result.changed) {
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: result.cart }));
      }
      return result;
    } catch (err) {
      console.error('[core-bundle] reconcile failed', err);
      return null;
    } finally {
      reconciling = false;
      if (pendingPass) {
        pendingPass = false;
        setTimeout(reconcile, 50);
      }
    }
  }

  // ---- Hook 1: central Empire event ----
  window.addEventListener('cartcount:update', function (e) {
    if (reconciling) return; // our own fire; ignore
    reconcile();
  });

  // ---- Hook 2: page load ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    setTimeout(reconcile, 0);
  }

  // ---- Hook 3: fetch wrapper safety net ----
  const _fetch = window.fetch;
  window.fetch = function (input, init) {
    const result = _fetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = (init && init.method) || (input && input.method) || 'GET';
      if (!reconciling && method.toUpperCase() === 'POST') {
        const matched = CART_PATHS.some(p => url.indexOf(p) !== -1);
        if (matched) {
          result.finally(function () { setTimeout(reconcile, 0); });
        }
      }
    } catch (e) { /* swallow */ }
    return result;
  };

  // ---- Hook 4: cart form submit (checkout + no-JS updates[]) ----
  function bindCartForm() {
    const form = document.querySelector('form[data-cart-form]')
              || document.querySelector('form[action="/cart"]')
              || document.querySelector('form[action$="/cart"]');
    if (!form || form.dataset.coreBundleBound === '1') return;
    form.dataset.coreBundleBound = '1';

    form.addEventListener('submit', async function (e) {
      // Reset any tampered-with core updates[] stubs to their original quantity.
      try {
        form.querySelectorAll('input[data-core-updates-stub]').forEach(function (input) {
          const qty = input.getAttribute('data-qty');
          if (qty !== null) input.value = qty;
        });
      } catch (err) { /* swallow */ }

      // Only block & reconcile for the checkout button; let plain quantity updates through.
      const submitter = e.submitter;
      const isCheckout = submitter && submitter.name === 'checkout';
      if (!isCheckout) return;

      e.preventDefault();
      const result = await reconcile();
      if (result && result.changed) {
        showAdjustedBanner(form);
        // Give the user a tick to see the banner, then submit.
        setTimeout(function () { resubmitForCheckout(form, submitter); }, 1200);
      } else {
        resubmitForCheckout(form, submitter);
      }
    });
  }

  // form.submit() drops the submitter button, so Shopify never sees
  // name="checkout" and redirects back to /cart. Add it as a hidden field
  // (or use requestSubmit, which preserves the submitter) before posting.
  function resubmitForCheckout(form, submitter) {
    if (typeof form.requestSubmit === 'function') {
      try { form.requestSubmit(submitter); return; } catch (e) { /* fall through */ }
    }
    if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = (submitter && submitter.value) || '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  function showAdjustedBanner(form) {
    let banner = document.querySelector('[data-core-adjusted-banner]');
    if (banner) { banner.hidden = false; return; }
    banner = document.createElement('div');
    banner.setAttribute('data-core-adjusted-banner', '');
    banner.className = 'core-bundle-banner';
    banner.setAttribute('role', 'status');
    banner.textContent = (window.__coreBundleStrings && window.__coreBundleStrings.adjusted)
      || 'We adjusted required parts in your cart.';
    form.parentNode.insertBefore(banner, form);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartForm);
  } else {
    bindCartForm();
  }

  // ---- Pre-rendered Liquid data (optional; used by submit stripping + banner) ----
  try {
    const el = document.getElementById('core-bundle-data');
    if (el) window.__coreBundleData = JSON.parse(el.textContent);
  } catch (e) { /* swallow */ }

  // Expose for debugging / manual trigger.
  window.coreBundle = { reconcile: reconcile };
})();
